import {useState} from "react";
import {Grid, Typography, useMediaQuery, useTheme} from "@mui/material";
import AccordionSetup from "../AccordionSetup";
import AddCircleOutlineOutlined from '@mui/icons-material/AddCircleOutlineOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import {CustomerDto, CustomerType, EventDto} from "../../../interfaces/IEvent";
import AffectedSnippet from "./AffectedSnippet";
import {useNavigate} from "react-router-dom";
import {AffectedInfoDialog} from "./AffectedInfoDialog";

interface AffectedProps {
    viewEvent: EventDto;
}

function AffectedEntitiesContainer({viewEvent}: AffectedProps){
    const theme = useTheme();
    const isXsOnly = useMediaQuery(theme.breakpoints.only('xs'));
    const [viewElement, setViewElement] = useState<{
        name: string;
        title: string;
        type: CustomerType | string;
        data: CustomerDto[] | string[];
    }>();
    const [open, setOpen] = useState(false);

    const affected = [
        {
            name: "Bedrift",
            title: "Berørte bedriftskunder",
            type: CustomerType.Company,
            data: viewEvent.affectedCustomers
                ?.filter(x => x.customerType === CustomerType.Company)
                .sort((a, b) => a.customerName.localeCompare(b.customerName))
        },
        {
            name: "Privat",
            title: "Berørte privatkunder",
            type: CustomerType.Private,
            data: viewEvent.affectedCustomers
                ?.filter(x => x.customerType === CustomerType.Private)
                .sort((a, b) => a.customerName.localeCompare(b.customerName)),
        },
        {
            name: "Ukjent",
            title: "Berørte ukjent",
            type: CustomerType.Undefined,
            data: viewEvent.affectedCustomers
                ?.filter(x => x.customerType === CustomerType.Undefined)
                .sort((a, b) => a.customerName?.localeCompare(b.customerName)),
        },
        {
            name: "SLA",
            title: "Berørte bedriftskunder m/SLA",
            type: CustomerType.CompanyWithSla,
            data: viewEvent.affectedCustomers
                ?.filter(x => x.customerType === CustomerType.CompanyWithSla)
                .sort((a, b) => a.customerName.localeCompare(b.customerName)),
        },
        {
            name: "Telefon",
            title: "Telefon",
            type: "Telefon",
            data: viewEvent.affectedPhoneNumbers,
        },
        {
            name: "Epost",
            title: "Epost",
            type: "Epost",
            data: viewEvent.affectedEmailAddresses,
        },
    ];


    const navigate = useNavigate();

    const goToAffectedCustomers = () => {
        navigate(`/events/${viewEvent.id}/affected`, { replace: false });
    };

    const countAffected = () => {
        let count = 0;

        affected.forEach(element => {
            if(element.data?.length > 0){
                count++;
            }
        });

        if(viewEvent.isAllCompanyCustomersAffected){
            count++;
        }

        return count;
    };

    const calculateGridColumns = () => {
        const totalItems = countAffected();

        if (totalItems === 1) {
            return 12;
        } else if (totalItems === 2) {
            return 6;
        } else if(totalItems === 3) {

            return 4;
        } else if (totalItems === 4) {
            if(isXsOnly){
                return 6;
            }
            return 3;
        }
        else {
            return 4;
        }
    };

    const handleSnippetClick = (element: {
        name: string;
        title: string;
        type: CustomerType | string;
        data: CustomerDto[] | string[];
    }) => {
        setOpen(true);
        setViewElement(element);
    }

    return (
        <>
            {viewElement &&
                <AffectedInfoDialog
                    title={viewElement.title}
                    customerType={viewElement.type}
                    customer={viewElement.data}
                    open={open}
                    onClose={() => setOpen(false)}
                />
            }
            <AccordionSetup
                title={"Berørte"}
                icon={<PeopleAltOutlinedIcon />}
                actionIcon={
                    viewEvent.networkTree?.length > 0 ||
                    viewEvent.affectedPhoneNumbers?.length > 0 ||
                    viewEvent.affectedEmailAddresses?.length > 0 ||
                    viewEvent.isAllCompanyCustomersAffected ?
                        <EditOutlinedIcon /> : <AddCircleOutlineOutlined />
                }
                action={goToAffectedCustomers}
            >
                {(affected?.filter(element => element.data?.length > 0).length > 0  || viewEvent.isAllCompanyCustomersAffected) &&
                    <Grid container spacing={2}>
                        {
                            affected.filter(element => element.data?.length > 0).length > 0 &&
                            affected.map((element, index) => {
                                return (element.data?.length > 0 && <Grid
                                    key={index}
                                    size={{
                                        xs: calculateGridColumns(),
                                        sm: calculateGridColumns()
                                    }}>
                                    <AffectedSnippet
                                        affectedName={element.name}
                                        affectedType={element.type}
                                        affectedCount={element.data.length}
                                        onSnippetClick={() => handleSnippetClick(element)}
                                    />
                                </Grid>);
                            })
                        }
                        {viewEvent.isAllCompanyCustomersAffected &&
                            <Grid
                                size={{
                                    xs: calculateGridColumns(),
                                    sm: calculateGridColumns()
                                }}>
                                <AffectedSnippet
                                    affectedName={"Bedrift"}
                                    affectedType={CustomerType.Company}
                                    affectedCount={"Alle"}
                                />
                            </Grid>
                        }
                    </Grid>}
                {affected?.filter(element => element.data?.length > 0).length <= 0 && !viewEvent.isAllCompanyCustomersAffected &&
                    <Typography variant="body2">Ingen berørte kunder.</Typography>
                }
            </AccordionSetup>
        </>
    );
}

export default AffectedEntitiesContainer;